import { useTranslation } from 'react-i18next'
import { Box, styled, Typography } from '@mui/material'
import { useIsSmallScreenFalse } from 'packages/core'
import { ListingGrid, GridContainer, Filters, Button } from 'components'
import useSubcomponents from 'useSubcomponents'
import EidTableGrid from 'components/EidTableGrid/EidTableGrid'
import {
    useAddItemsToCart,
    useCart,
    useOtherManagementRoleSetGroups,
} from 'hooks'
import { useState } from 'react'
import { Tooltip, WarningModal } from 'packages/eid-ui'
import { BulkRemoveButton } from 'components/BulkActionsButtons/BulkRemoveButton'
import AddNewSetGroup from './AddNewSetGroups'
import cartHelpers from 'containers/Cart/cartHelpers'

const canDeleteMultiple =
    'ResourceAdmin-ManagementRoleDetails-OtherManagementRoles-SetGroups-CanDeletMultiple'

const canAddSetGroupAsMember =
    'ResourceAdmin-ManagementRoleDetails-OtherManagementRoles-SetGroups-CanAddSetGroup'

const SearchMenuBarFilter = styled(Box)({
    width: '100%',

    '& svg': {
        color: ' #aab0b4',
        marginRight: '3px',
    },
    '& div:nth-child(1)': {
        boxShadow: '0 4px 6px -2px rgba(0, 0, 0, 0.1) !important',
        border: 'solid 1px #ebebed',
        paddingLeft: '0',
        height: '40px',
    },
    '& ::placeholder': {
        color: '#aab0b4',
    },
})

const DeleteButton = styled(Box)({
    paddingRight: '10px',
    '& button': {
        padding: '8px 12px',
        borderRadius: ' 4px',
        border: 'solid 1px #ff4d5f',
        backgroundColor: '#fff',
    },
})

const OtherTypesOfManagementRoleMembersSetGroups = ({ managementRoleId }) => {
    const { t } = useTranslation()
    const { getAccessBasedAttributes } = useSubcomponents()
    const [selectedIds, setSelectedIds] = useState([])
    const [selectedItemsToDelete, setSelectedItemsToDelete] = useState(null)
    const { data: cart } = useCart()
    const [addItemsToCart, { isLoading: isDeleting }] = useAddItemsToCart()
    const { hasAccessToControl } = useSubcomponents()
    const {
        list,
        loading: isLoading,
        loadingMore: isLoadingMore,
        pagination,
    } = useOtherManagementRoleSetGroups(managementRoleId)

    const isSmallScreen = useIsSmallScreenFalse()

    const attributes = [
        {
            name: 'friendlyName',
            sortable: true,
            sortBy: 'friendlyName',
            label: 'FriendlyName',
            isHeader: true,
            showInCard: false,
            requireAccess: {
                control:
                    'ResourceAdmin-ManagementRoleDetails-OtherManagementRoles-SetGroups-FriendlyName',
            },
        },
        {
            name: 'name',
            sortable: true,
            sortBy: 'name',
            label: 'Name',
            requireAccess: {
                control:
                    'ResourceAdmin-ManagementRoleDetails-OtherManagementRoles-SetGroups-Name',
            },
        },
        {
            name: 'isCollectionOfPeople',
            sortable: true,
            sortBy: 'isCollectionOfPeople',
            label: 'IsCollectionOfPeople',
            hideValue: true,
            requireAccess: {
                control:
                    'ResourceAdmin-ManagementRoleDetails-OtherManagementRoles-SetGroups-IsCollectionOfPeople',
            },
            component: {
                componentName: 'renderer',
                render: (data) => (
                    <Typography
                        style={{ fontSize: '13px', fontWeight: 'normal' }}
                    >
                        {data?.isCollectionOfPeople ? t('Yes') : t('No')}
                    </Typography>
                ),
            },
        },
        {
            name: 'description',
            sortable: true,
            sortBy: 'description',
            label: 'Description',
            requireAccess: {
                control:
                    'ResourceAdmin-ManagementRoleDetails-OtherManagementRoles-SetGroups-Description',
            },
        },

        {
            hideValue: true,
            requireAccess: {
                control:
                    'ResourceAdmin-ManagementRoleDetails-OtherManagementRoles-SetGroups-DeleteAction',
            },
            component: {
                componentName: 'renderer',
                render: (data) => {
                    return (
                        <Tooltip
                            title={
                                checkIfAlreadyAddedToCart(data?.id)
                                    ? t('Common_ItemAlreadyInCart')
                                    : ''
                            }
                        >
                            <DeleteButton>
                                <Button.Delete
                                    showEndIcon={false}
                                    onClick={() => handleSingleDelete(data)}
                                    disabled={checkIfAlreadyAddedToCart(
                                        data?.id,
                                    )}
                                />
                            </DeleteButton>
                        </Tooltip>
                    )
                },
            },
        },
    ]

    const checkIfAlreadyAddedToCart = (itemrequestableResourceId) => {
        return cartHelpers.isItemAlreadyInCart(cart, itemrequestableResourceId)
    }

    const handleSingleDelete = (item) => {
        setSelectedItemsToDelete([item])
    }

    const getCartItem = (item) => {
        const cartItem = cartHelpers.removeSingleAssignment({
            targetAssigneeId: managementRoleId,
            resource: { ...item, resourceType_Name: item.resourceTypeName },
        })
        return cartItem
    }

    const handleDeleteConfirm = () => {
        try {
            let cartItems = []
            selectedItemsToDelete.forEach((element) => {
                cartItems.push(getCartItem(element))
            })
            addItemsToCart(cartItems).then(() => {
                setSelectedIds([])
                setSelectedItemsToDelete(null)
            })
        } catch (err) {}
    }

    const handleRowSelection = (item) => {
        let selectedItems = [...selectedIds]
        const selectedIndex = selectedIds.findIndex((id) => id === item.id)
        if (selectedIndex >= 0) {
            const filteredList = selectedItems.filter((x) => {
                return x !== item.id
            })
            setSelectedIds(filteredList)
        } else {
            setSelectedIds([...selectedItems, item.id])
        }
    }

    const handleSelectAllClick = () => {
        if (selectedIds.length === list.length) {
            setSelectedIds([])
        } else {
            const filteredListNotAssigned = list.filter((item) => {
                if (!checkIfAlreadyAddedToCart(item?.id)) {
                    return item
                }
            })
            const filteredList = filteredListNotAssigned?.length
                ? filteredListNotAssigned.map((item) => item.id)
                : []

            setSelectedIds(filteredList)
        }
    }

    const selectAllAction = hasAccessToControl(canDeleteMultiple)
        ? {
              style: {
                  padding: '0px !important',
              },
              render: () => {
                  return (
                      <BulkRemoveButton
                          selectedCount={selectedIds.length}
                          label={t('RemoveSelectedSetGroups', {
                              count: selectedIds.length,
                          })}
                          onClick={handleBulkAction}
                          dataProtectedsubcomponent={canDeleteMultiple}
                      />
                  )
              },
          }
        : null

    const handleBulkAction = () => {
        const selectedItems = list.filter((i) => selectedIds.includes(i.id))
        setSelectedItemsToDelete(selectedItems)
    }

    return (
        <Box>
            <ListingGrid>
                <Box
                    display="flex"
                    flexDirection={isSmallScreen ? 'column' : 'row'}
                    justifyContent="space-between"
                >
                    {hasAccessToControl(canAddSetGroupAsMember) ? (
                        <AddNewSetGroup
                            protectedsubcomponent={canAddSetGroupAsMember}
                            managementRoleId={managementRoleId}
                        />
                    ) : (
                        <Box></Box>
                    )}
                    <Box width="240px" display="flex" justifyContent="flex-end">
                        <SearchMenuBarFilter>
                            <Filters.TextSearch outlined />
                        </SearchMenuBarFilter>
                    </Box>
                </Box>
                <GridContainer
                    style={{
                        padding: '16px 0px',
                        marginTop: '0px',
                        backgroundColor: isSmallScreen
                            ? 'white'
                            : 'transparent',
                    }}
                >
                    <EidTableGrid
                        selectAllAction={selectAllAction}
                        rowSelection={hasAccessToControl(canDeleteMultiple)}
                        handleClick={handleRowSelection}
                        handleSelectAllClick={handleSelectAllClick}
                        selected={selectedIds}
                        noDataMessage={t('NoDataMessage')}
                        pagination={pagination}
                        isLoading={isLoading}
                        isLoadingMore={isLoadingMore}
                        attributes={getAccessBasedAttributes(attributes)}
                        list={
                            (list?.length &&
                                list.map((item) => {
                                    return {
                                        ...item,
                                        isAlreadyAssigned:
                                            checkIfAlreadyAddedToCart(item?.id),
                                    }
                                })) ||
                            []
                        }
                    />
                </GridContainer>
            </ListingGrid>
            {selectedItemsToDelete && (
                <WarningModal
                    title={
                        selectedItemsToDelete.length === 1
                            ? `${t('Delete')} ${
                                  selectedItemsToDelete[0]?.friendlyName
                              }`
                            : t('DeleteSelectedItems')
                    }
                    description={
                        selectedItemsToDelete.length === 1
                            ? t('OtherManagementRoleDeleteSetGroup')
                            : t(
                                  'OtherManagemnetRoleSetGroupDeleteSelectedMessage',
                              )
                    }
                    yesLabel={t('Delete')}
                    noLabel={t('Cancel')}
                    open={selectedItemsToDelete}
                    cancel={() => {
                        setSelectedItemsToDelete(null)
                    }}
                    proceeding={isDeleting}
                    proceed={handleDeleteConfirm}
                />
            )}
        </Box>
    )
}

export default OtherTypesOfManagementRoleMembersSetGroups
